import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Clock, Coffee, LogOut, Play, MapPin, Loader2, AlertTriangle, CheckCircle2, WifiOff } from "lucide-react";
import { newEventId, readLocationOnce, RESULT_LABEL } from "@/lib/clockLocation";

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const varianceLabel = (mins) => {
  if (mins === null || mins === undefined) return null;
  if (Math.abs(mins) <= 5) return { text: "On time", cls: "text-emerald-600" };
  if (mins > 5) return { text: `Started ${mins} min late`, cls: "text-amber-600" };
  return { text: `Started ${Math.abs(mins)} min early`, cls: "text-sky-600" };
};
const isNetworkError = (e) => !e?.response;

function GeoException({ geo, busy, onSubmit, onCancel }) {
  const [reason, setReason] = useState("");
  const word = geo.action === "start" ? "clock-in" : "clock-out";
  return (
    <div className="mt-3 border border-amber-200 bg-amber-50 rounded-xl p-3 space-y-2" data-testid="geo-exception-panel">
      <div className="text-sm font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle size={14} /> Not clocked {geo.action === "start" ? "in" : "out"} yet — {RESULT_LABEL[geo.result] || geo.result}</div>
      <p className="text-xs text-amber-800" data-testid="geo-exception-message">{geo.message}</p>
      {geo.distance_m != null && <p className="text-[11px] text-amber-700" data-testid="geo-exception-distance">About {geo.distance_m} m from the site (reading accuracy ±{geo.accuracy_m} m; allowed radius {geo.radius_m} m).</p>}
      <textarea className="w-full border rounded-lg p-2 text-sm" rows={2} maxLength={500} placeholder={`Why can't your location be verified? (required for a ${word} exception)`} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="geo-exception-reason" />
      <div className="flex gap-2">
        <Button size="sm" className="bg-amber-600 hover:bg-amber-700" disabled={busy || !reason.trim()} onClick={() => onSubmit(reason.trim())} data-testid="geo-exception-submit">Submit {word} exception</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={onCancel} data-testid="geo-exception-cancel">Cancel</Button>
      </div>
      <p className="text-[11px] text-amber-700">Your {word} time is the moment you first tapped. A manager reviews exceptions before the time goes to payroll.</p>
    </div>
  );
}

export const ShiftStatusCard = () => {
  const [state, setState] = useState(null); // {linked, attendance, break, last_completed}
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(localStorage.getItem("cc_site") && localStorage.getItem("cc_site") !== "all" ? localStorage.getItem("cc_site") : "");
  const [, setTick] = useState(0);
  const [geo, setGeo] = useState(null);
  const [locating, setLocating] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/workforce/attendance/me/current");
      setState(r.data); setOffline(false);
    } catch (e) {
      if (isNetworkError(e)) setOffline(true);
      setState((s) => s || { linked: true, attendance: null });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); api.get("/me/sites").then((r) => setSites(r.data.sites || [])).catch(() => {}); }, [load]);
  // Live elapsed clock while a shift is active (display only — totals are server-authoritative).
  useEffect(() => {
    if (!state?.attendance) return;
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, [state?.attendance]);

  const call = async (fn) => {
    setBusy(true);
    try { await fn(); setOffline(false); await load(); }
    catch (e) {
      if (isNetworkError(e)) { setOffline(true); toast.error("Shift clocking needs an internet connection."); }
      else { toast.error(e.response?.data?.detail || "Something went wrong"); throw e; }
    } finally { setBusy(false); }
  };

  // Location is read once, only after an explicit tap, and only when the site has geofencing enabled.
  const clock = async (action, extra = {}, prepared = null) => {
    const url = `/workforce/attendance/${action}`;
    const site = action === "start" ? (siteId || (sites.length === 1 ? (sites[0].venue_id || sites[0].site_id) : "")) : state?.attendance?.site_id;
    let payload = prepared || { client_event_id: newEventId(), ...(action === "start" && siteId ? { site_id: siteId } : {}), ...extra };
    let located = !!prepared;
    if (!prepared && site) {
      const g = await api.get(`/workforce/attendance/geofence-status?site_id=${site}`).then((r) => r.data).catch(() => null);
      if (g?.enabled) { setLocating(true); payload = { ...payload, ...(await readLocationOnce()) }; setLocating(false); located = true; }
    }
    try { return await api.post(url, payload); }
    catch (e) {
      const gf = e?.response?.status === 409 && e.response.data?.geofence;
      if (gf && gf.result === "no_location" && !located) {
        setLocating(true); const loc = await readLocationOnce(); setLocating(false);
        return clock(action, extra, { ...payload, ...loc });
      }
      if (gf) { setGeo({ ...gf, message: e.response.data.detail, payload }); e.handled = true; }
      throw e;
    }
  };
  const done = (r, verb) => {
    const l = r?.data?.location;
    setGeo(null);
    if (l?.exception_submitted) toast.success(`${verb} exception submitted — your time is recorded and waits for manager review`);
    else toast.success(l?.review === "verified" ? `${verb} — location verified` : verb);
  };
  const start = () => call(async () => {
    try { done(await clock("start"), "Clocked in"); } catch (e) { if (!e.handled) throw e; }
  });
  const endShift = () => call(async () => {
    try { done(await clock("end"), "Clocked out"); }
    catch (e) {
      if (e.handled) return;
      if (!isNetworkError(e) && /break/i.test(e.response?.data?.detail || "")) {
        if (window.confirm("You're still on a break. End the shift and close the break now?")) {
          try { done(await clock("end", { force_end_break: true }), "Clocked out"); } catch (e2) { if (!e2.handled) throw e2; }
        } else { return; }
      } else { throw e; }
    }
  });
  const submitException = (reason) => call(async () => {
    const r = await api.post(`/workforce/attendance/${geo.action}`, { ...geo.payload, exception_reason: reason });
    done(r, geo.action === "start" ? "Clock-in" : "Clock-out");
  });
  const startBreak = () => call(async () => { await api.post("/workforce/attendance/break/start", {}); toast.success("Break started"); });
  const endBreak = () => call(async () => { await api.post("/workforce/attendance/break/end", {}); toast.success("Break ended"); });

  if (loading) return <div className="bg-white border rounded-2xl p-5 flex items-center gap-2 text-slate-400" data-testid="shift-card-loading"><Loader2 size={16} className="animate-spin" /> Loading shift…</div>;
  if (state && state.linked === false) return null; // only for linked workforce people

  const att = state?.attendance;
  const onBreak = !!state?.break;
  const needsReview = state?.needs_review || att?.status === "NEEDS_REVIEW";
  const geoBlock = geo && <GeoException geo={geo} busy={busy} onSubmit={submitException} onCancel={() => setGeo(null)} />;
  const locNote = locating && <p className="text-xs text-slate-500 mt-2 flex items-center gap-1" data-testid="geo-locating"><MapPin size={12} /> This site checks your location once when you clock in or out. Reading it now…</p>;

  if (offline && !att) {
    return (
      <div className="bg-white border rounded-2xl p-5" data-testid="shift-card-offline">
        <div className="flex items-center gap-2 text-slate-600 font-semibold"><WifiOff size={18} /> Shift clocking</div>
        <p className="text-sm text-slate-500 mt-2">Shift clocking needs an internet connection.</p>
        <Button variant="outline" className="mt-3" onClick={load} data-testid="shift-retry-btn">Retry</Button>
      </div>
    );
  }

  // NOT CLOCKED IN
  if (!att) {
    const last = state?.last_completed;
    const multi = sites.length > 1;
    return (
      <div className="bg-white border rounded-2xl p-5" data-testid="shift-card-idle">
        <div className="flex items-center gap-2 text-slate-800 font-bold"><Clock size={18} className="text-brand" /> Not clocked in</div>
        {last && (
          <p className="text-xs text-slate-500 mt-1" data-testid="shift-last-summary">
            Last shift: {last.business_date} · {last.actual_hours}h at {last.site_name}
          </p>
        )}
        {multi && (
          <select className="mt-3 w-full border rounded-lg p-2 text-sm" value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="shift-site-select">
            <option value="">Choose a site…</option>
            {sites.map((s) => <option key={s.venue_id || s.site_id} value={s.venue_id || s.site_id}>{s.name}</option>)}
          </select>
        )}
        <Button className="mt-3 w-full h-12 bg-brand hover:bg-brand-hover font-bold" onClick={start} disabled={busy || (multi && !siteId)} data-testid="shift-start-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <><Play size={16} className="mr-1" /> Start shift</>}
        </Button>
        {locNote}{geoBlock}
      </div>
    );
  }

  const v = varianceLabel(att.start_variance_minutes);
  // ON BREAK
  if (onBreak) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="shift-card-onbreak">
        <div className="flex items-center gap-2 text-amber-700 font-bold"><Coffee size={18} /> On break</div>
        <p className="text-sm text-amber-700 mt-1">Break started at {fmtTime(state.break.started_at_utc)}</p>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <Button className="h-11 bg-amber-600 hover:bg-amber-700 font-semibold" onClick={endBreak} disabled={busy} data-testid="break-end-btn"><Coffee size={16} className="mr-1" /> End break</Button>
          <Button variant="outline" className="h-11 font-semibold" onClick={endShift} disabled={busy} data-testid="shift-end-btn"><LogOut size={16} className="mr-1" /> End shift</Button>
        </div>
        {locNote}{geoBlock}
      </div>
    );
  }

  // ACTIVE
  return (
    <div className="bg-white border rounded-2xl p-5" data-testid="shift-card-active">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-emerald-700 font-bold"><CheckCircle2 size={18} /> Working</div>
        {needsReview && <span className="text-xs bg-rose-50 text-rose-600 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid="shift-needs-review"><AlertTriangle size={12} /> Needs review</span>}
        {att.unscheduled && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full" data-testid="shift-unscheduled">Unscheduled</span>}
        {att.start_location_review === "pending" && <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full" data-testid="shift-location-pending">Clock-in exception awaiting review</span>}
      </div>
      <p className="text-sm text-slate-600 mt-1">Since {fmtTime(att.started_at_utc)}</p>
      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={12} /> {att.site_name || "—"}</p>
      {att.scheduled_start && <p className="text-xs text-slate-500 mt-0.5" data-testid="shift-scheduled">Scheduled {att.scheduled_start}–{att.scheduled_end}</p>}
      {v && <p className={`text-xs mt-0.5 ${v.cls}`} data-testid="shift-variance">{v.text}</p>}
      <div className="grid grid-cols-2 gap-2 mt-3">
        <Button variant="outline" className="h-11 font-semibold" onClick={startBreak} disabled={busy} data-testid="break-start-btn"><Coffee size={16} className="mr-1" /> Start break</Button>
        <Button className="h-11 bg-slate-800 hover:bg-slate-900 font-semibold" onClick={endShift} disabled={busy} data-testid="shift-end-btn"><LogOut size={16} className="mr-1" /> End shift</Button>
      </div>
      {locNote}{geoBlock}
    </div>
  );
};

export default ShiftStatusCard;
